import React from "react";
import * as LucideIcons from "lucide-react";
import MioloNo from "./MioloNo";

/**
 * NoFluxo — card de uma etapa da operação.
 *
 * O dado diz apenas o `tipo`; cor e peso visual saem daqui. Cores seguem os
 * acentos que a landing ja usa: verde da marca pra automacao e conclusao, roxo
 * pra equipe, ambar pra decisao, azul pro cliente, ciano pra transacao.
 *
 * `ativo` marca a etapa que esta acontecendo agora — a secao avanca esse
 * estado em sequencia, e e isso que da a leitura de "operacao executando" em
 * vez de "diagrama parado".
 *
 * Unico consumidor: FluxoVertical (mobile/tablet), que empilha os cards em
 * largura total. O modo posicionado em palco absoluto saiu junto com o
 * layout horizontal generico.
 */

export const TIPOS = {
  cliente: { rgb: "59,130,246", rotulo: "Cliente" },
  ia: { rgb: "34,197,94", rotulo: "IA do Evolzap" },
  sistema: { rgb: "125,155,190", rotulo: "Sistema" },
  decisao: { rgb: "245,158,11", rotulo: "Decisão" },
  acao: { rgb: "6,182,212", rotulo: "Venda" },
  humano: { rgb: "168,85,247", rotulo: "Equipe" },
  sucesso: { rgb: "34,197,94", rotulo: "Concluído" },
};

const Icone = ({ nome, ...props }) => {
  const C = LucideIcons[nome] || LucideIcons.Circle;
  return <C {...props} />;
};

export default function NoFluxo({
  etapa,
  ordem = 0,
  ativo = false,
  atenuado = false,
}) {
  const t = TIPOS[etapa.tipo] || TIPOS.sistema;

  return (
    <div
      className={`av-surgir av-no relative flex w-full flex-col gap-2 rounded-2xl p-3 ${
        ativo ? "av-no-ativo" : ""
      } ${atenuado ? "av-no-atenuado" : ""}`}
      style={{
        "--av-atraso": `${160 + ordem * 110}ms`,
        "--av-rgb": t.rgb,
        // O card se separa do palco por LUMINOSIDADE e sombra, não por um
        // contorno forte — borda grossa faz a trilha virar "caixas ligadas por
        // linha". O ativo ganha a cor do tipo; os demais quase não têm borda.
        background: ativo
          ? "linear-gradient(160deg, #272D2B 0%, #1E2322 100%)"
          : "linear-gradient(160deg, #21262500 0%, #1A1F1E 100%)",
        backgroundColor: ativo ? "#232928" : "#1C2120",
        border: `1px solid ${ativo ? `rgba(${t.rgb},0.5)` : "rgba(255,255,255,0.075)"}`,
        boxShadow: ativo
          ? `0 20px 44px -18px rgba(0,0,0,0.95), 0 0 26px -10px rgba(${t.rgb},0.3)`
          : "0 16px 34px -18px rgba(0,0,0,0.9)",
        backdropFilter: "blur(6px)",
      }}
      data-testid={`no-${etapa.id}`}
      data-ativo={ativo ? "true" : "false"}
    >
      {/* Brilho de 1px no topo — mesmo detalhe dos cards do resto do site */}
      <span
        className="pointer-events-none absolute inset-x-3 top-0 h-px rounded-full"
        style={{
          background: ativo
            ? `linear-gradient(90deg, transparent, rgba(${t.rgb},0.5), transparent)`
            : "linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)",
        }}
        aria-hidden="true"
      />

      {/* Cabeçalho: papel da etapa + título */}
      <div className="flex items-start gap-2">
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
          style={{
            background: `rgba(${t.rgb},0.14)`,
            border: `1px solid rgba(${t.rgb},0.3)`,
          }}
        >
          <Icone nome={etapa.icone} className="h-3.5 w-3.5" style={{ color: `rgb(${t.rgb})` }} />
        </span>
        <span className="min-w-0 flex-1 leading-tight">
          <span
            className="block text-[9px] font-bold uppercase tracking-[0.14em]"
            style={{ color: `rgba(${t.rgb},0.9)` }}
          >
            {etapa.papel || t.rotulo}
          </span>
          <span className="block text-[11.5px] font-semibold leading-tight text-white">
            {etapa.titulo}
          </span>
        </span>
        {ativo && (
          <span
            className="av-pulso mt-2 h-1.5 w-1.5 shrink-0 rounded-full"
            style={{ background: `rgb(${t.rgb})` }}
            aria-hidden="true"
          />
        )}
      </div>

      {/* Miolo: a operação real desta etapa */}
      <MioloNo etapa={etapa} />
    </div>
  );
}
